import type { ReactNode } from "react";

type Props = {
  id?: string;
  index?: string;
  eyebrow?: string;
  title?: ReactNode;
  children: ReactNode;
  dark?: boolean;
  className?: string;
  variant?: "screen" | "pdf";
  /** Smaller header for sections sharing a page */
  compact?: boolean;
  /** Section lives inside a pdf-page-stack (no full-page height) */
  stacked?: boolean;
};

export function Section({
  id,
  index,
  eyebrow,
  title,
  children,
  dark,
  className = "",
  variant = "screen",
  compact = false,
  stacked = false,
}: Props) {
  const header = (eyebrow || index) && (
    <div className={variant === "pdf" ? "pdf-section-block__eyebrow-row" : "report-caption mb-6 flex items-center gap-3 uppercase"}>
      {variant === "pdf" ? (
        <div className="pdf-section-block__eyebrow">
          {index && <span className="pdf-section-block__eyebrow-index">{index}</span>}
          {eyebrow && (
            <>
              <span className="pdf-section-block__eyebrow-line" />
              <span>{eyebrow}</span>
            </>
          )}
        </div>
      ) : (
        <>
          {index && (
            <span className={dark ? "text-white/50" : "text-muted-foreground"}>{index}</span>
          )}
          {eyebrow && (
            <>
              <span className={`h-px w-8 ${dark ? "bg-white/20" : "bg-foreground/15"}`} />
              <span className={dark ? "text-white/70" : "text-foreground/70"}>{eyebrow}</span>
            </>
          )}
        </>
      )}
    </div>
  );

  const titleEl = title && (
    <h2
      className={
        variant === "pdf"
          ? "pdf-section-block__title"
          : `report-h2 ${dark ? "text-white" : ""}`
      }
    >
      {title}
    </h2>
  );

  if (variant === "pdf") {
    return (
      <div
        id={id}
        className={[
          "pdf-section-block",
          compact ? "pdf-section-block--compact" : "",
          stacked ? "pdf-section-block--stacked" : "",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="pdf-section-block__head">
          {header}
          {titleEl}
        </div>
        <div className="pdf-section-block__body">{children}</div>
      </div>
    );
  }

  return (
    <section
      id={id}
      data-pdf-section="true"
      className={`relative w-full ${dark ? "bg-navy-deep text-white" : "bg-signal-soft"} ${className}`}
    >
      <div className="report-section-shell mx-auto w-full max-w-6xl px-6 py-24 md:py-32">
        {header}
        {titleEl}
        <div className="mt-12">{children}</div>
      </div>
    </section>
  );
}
